"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ItemLayerError = exports.itemClaimMessage = exports.itemSlotOfTrait = exports.itemIdOf = exports.itemValue = exports.ITEM_SLOTS = exports.ITEM_SLOT_TRAIT = exports.ITEM_CANVAS = exports.ITEM_LAYER_MAX_BYTES = exports.ITEM_MAX_LAYERS = void 0;
exports.isItemWindowOpen = isItemWindowOpen;
exports.canWearItem = canWearItem;
exports.itemLockedMessage = itemLockedMessage;
exports.sanitizeItemSvg = sanitizeItemSvg;
exports.sanitizeItemLayers = sanitizeItemLayers;
exports.ITEM_MAX_LAYERS = 4;
exports.ITEM_LAYER_MAX_BYTES = 20 * 1024;
exports.ITEM_CANVAS = { width: 264, height: 280 };
/** The avatar trait each item slot is stored in. */
exports.ITEM_SLOT_TRAIT = { headwear: 'headwear', accessory: 'prop' };
exports.ITEM_SLOTS = Object.keys(exports.ITEM_SLOT_TRAIT);
const ITEM_PREFIX = 'item:';
const ITEM_VALUE = /^item:[0-9a-f-]{36}$/;
/** The trait value an avatar stores to wear an item. */
const itemValue = (id) => `${ITEM_PREFIX}${id}`;
exports.itemValue = itemValue;
/** The item id in a trait value, or null when the value is a built-in option. */
const itemIdOf = (value) => (typeof value === 'string' && ITEM_VALUE.test(value) ? value.slice(ITEM_PREFIX.length) : null);
exports.itemIdOf = itemIdOf;
/** The slot whose trait this is, or null for a trait items cannot go in. */
const itemSlotOfTrait = (trait) => { var _a; return (_a = exports.ITEM_SLOTS.find(slot => exports.ITEM_SLOT_TRAIT[slot] === trait)) !== null && _a !== void 0 ? _a : null; };
exports.itemSlotOfTrait = itemSlotOfTrait;
/** Whether a timed item can be claimed right now. */
function isItemWindowOpen(item, now = new Date()) {
    if (!item.availableFrom || !item.availableUntil)
        return false;
    return new Date(item.availableFrom) <= now && now < new Date(item.availableUntil);
}
/** Whether this user may wear the item: free, already theirs, or a timed item inside its window. */
function canWearItem(item, ownedItemIds, now = new Date()) {
    if (item.unlock === 'free' || ownedItemIds.includes(item.id))
        return true;
    return item.unlock === 'timed' && isItemWindowOpen(item, now);
}
const formatDay = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
/** What the editor says about an item the user cannot wear yet. */
function itemLockedMessage(item, now = new Date()) {
    if (item.unlock === 'reward')
        return 'Locked: win it in a challenge to unlock';
    if (item.availableFrom && new Date(item.availableFrom) > now)
        return `Locked: available from ${formatDay(item.availableFrom)}`;
    return 'Locked: this was a limited-time item';
}
/** The note under a timed item that can be claimed now. */
const itemClaimMessage = (item) => (item.unlock === 'timed' && item.availableUntil ? `Limited time: save your avatar with it by ${formatDay(item.availableUntil)} to keep it` : null);
exports.itemClaimMessage = itemClaimMessage;
const ALLOWED_ELEMENTS = new Set([
    'g', 'path', 'circle', 'ellipse', 'rect', 'line', 'polyline', 'polygon',
    'defs', 'lineargradient', 'radialgradient', 'stop', 'clippath', 'mask', 'use', 'title', 'desc',
]);
const SVG_WRAPPER = /^<svg\b([^>]*)>([\s\S]*)<\/svg>$/i;
class ItemLayerError extends Error {
}
exports.ItemLayerError = ItemLayerError;
/**
 * Checks an uploaded item layer and returns the markup to store: plain
 * vector shapes only, drawn on the avatar canvas. A whole exported .svg file
 * is accepted and unwrapped. Throws ItemLayerError with a message for the
 * person uploading.
 */
function sanitizeItemSvg(input) {
    let svg = input.replace(/<\?xml[\s\S]*?\?>/g, '').replace(/<!--[\s\S]*?-->/g, '').trim();
    if (!svg)
        throw new ItemLayerError('The layer is empty');
    if (svg.length > exports.ITEM_LAYER_MAX_BYTES)
        throw new ItemLayerError('The layer is larger than 20 KB');
    if (/<!|<\?|&#|&[a-z]+;/i.test(svg))
        throw new ItemLayerError('Doctype, entities and processing instructions are not allowed');
    const wrapper = SVG_WRAPPER.exec(svg);
    if (wrapper) {
        const viewBox = /viewBox\s*=\s*["']\s*0[\s,]+0[\s,]+(\d+(?:\.\d+)?)[\s,]+(\d+(?:\.\d+)?)\s*["']/i.exec(wrapper[1]);
        if (!viewBox || Number(viewBox[1]) !== exports.ITEM_CANVAS.width || Number(viewBox[2]) !== exports.ITEM_CANVAS.height) {
            throw new ItemLayerError(`The SVG must use viewBox="0 0 ${exports.ITEM_CANVAS.width} ${exports.ITEM_CANVAS.height}"`);
        }
        svg = wrapper[2].trim();
    }
    for (const tag of svg.matchAll(/<\/?\s*([A-Za-z][\w:.-]*)/g)) {
        if (!ALLOWED_ELEMENTS.has(tag[1].toLowerCase()))
            throw new ItemLayerError(`<${tag[1]}> is not allowed: use plain vector shapes only`);
    }
    if (/\son[a-z]+\s*=/i.test(svg))
        throw new ItemLayerError('Event attributes are not allowed');
    if (/\sstyle\s*=/i.test(svg))
        throw new ItemLayerError('Inline style attributes are not allowed: use presentation attributes (fill, stroke, opacity)');
    for (const href of svg.matchAll(/href\s*=\s*(["'])(.*?)\1/gi)) {
        if (!href[2].startsWith('#'))
            throw new ItemLayerError('Links to anything outside the layer are not allowed');
    }
    for (const url of svg.matchAll(/url\(\s*["']?([^)"']*)/gi)) {
        if (!url[1].startsWith('#'))
            throw new ItemLayerError('References to anything outside the layer are not allowed');
    }
    return svg;
}
/** Returns the layers ready to store, or throws ItemLayerError naming the layer at fault. */
function sanitizeItemLayers(input) {
    if (!Array.isArray(input))
        throw new ItemLayerError('Layers must be a list');
    if (input.length > exports.ITEM_MAX_LAYERS)
        throw new ItemLayerError(`An item can have at most ${exports.ITEM_MAX_LAYERS} layers`);
    return input.map((layer, index) => {
        const position = layer === null || layer === void 0 ? void 0 : layer.position;
        if (position !== 'back' && position !== 'front')
            throw new ItemLayerError(`Layer ${index + 1}: choose back or front`);
        if (typeof layer.svg !== 'string')
            throw new ItemLayerError(`Layer ${index + 1}: missing SVG`);
        try {
            return { position, svg: sanitizeItemSvg(layer.svg) };
        }
        catch (err) {
            if (err instanceof ItemLayerError)
                throw new ItemLayerError(`Layer ${index + 1}: ${err.message}`);
            throw err;
        }
    });
}
